import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import maxBy from 'lodash/maxBy';

import { findCustomValue } from '../customValuesUtils';
import { todayDateTime } from '../dateutils';
import { roundAtSecondDecimal } from '../numbers';
import { isNilOrEmpty } from '../objectUtils';
import {
  CommunicationChannel,
  CommunicationStatus,
  CommunicationType,
  InvoiceDeliveryChannel,
  InvoiceStatus,
  type Communication,
  type CustomValue,
  type CustomValueDTO,
  InvoiceType,
  PostingType,
  type Invoice,
  type InvoiceSentChannelInput,
  type InvoiceSentVersion,
  type Posting,
} from '../types';
import { InvoicePeppolStatus } from '../types/dto-admintool';

import { ACCOUNT_LEASE_TOPCLASS } from './accountClasses';
import {
  InvoicePeppolCustomValueName,
  PeppolErrorType,
  isPeppolResendInFlight,
  isPeppolSendClaimHeld,
} from './peppolUtils';

export enum InvoiceLifecycleErrorCode {
  NOT_FOUND = 'NOT_FOUND',
  WRONG_CLIENT = 'WRONG_CLIENT',
  NO_POLICY = 'NO_POLICY',
  NOT_DRAFT = 'NOT_DRAFT',
  NOT_VALIDATED = 'NOT_VALIDATED',
  REVERSED = 'REVERSED',
  PAYMENT_LINKED = 'PAYMENT_LINKED',
  EXACT_ONLINE_SYNCED = 'EXACT_ONLINE_SYNCED',
  PEPPOL_IN_PROGRESS = 'PEPPOL_IN_PROGRESS',
  EARLY_DRAFT_NOT_DELETABLE = 'EARLY_DRAFT_NOT_DELETABLE',
  EARLY_DRAFT_PENDING_REGENERATION = 'EARLY_DRAFT_PENDING_REGENERATION',
  NUMBER_TAKEN = 'NUMBER_TAKEN',
  NOT_SENDABLE = 'NOT_SENDABLE',
  NO_EMAIL_RECIPIENT = 'NO_EMAIL_RECIPIENT',
  SENDING_DISABLED = 'SENDING_DISABLED',
  DOCUMENT_NOT_READY = 'DOCUMENT_NOT_READY',
  INVALID_PARTIES = 'INVALID_PARTIES',
  CONFLICT = 'CONFLICT',
  TOO_LARGE = 'TOO_LARGE',
  IN_STATEMENT = 'IN_STATEMENT',
  SENT_NUMBER_LOCKED = 'SENT_NUMBER_LOCKED',
  ANTERIOR_PERIOD_VAT_LEASE = 'ANTERIOR_PERIOD_VAT_LEASE',
}

export const INVOICE_LIFECYCLE_ERROR_PREFIX = 'InvoiceLifecycle:';

const INVOICE_LIFECYCLE_ERROR_PATTERN = new RegExp(`${INVOICE_LIFECYCLE_ERROR_PREFIX}([A-Z_]+)`);

export const INVOICE_DELIVERED_COMMUNICATION_STATUSES: CommunicationStatus[] = [
  CommunicationStatus.SENT,
  CommunicationStatus.SENT_MANUALLY,
  CommunicationStatus.DOWNLOADED,
];

export const INVOICE_SENT_COMMUNICATION_STATUSES: CommunicationStatus[] = [
  ...INVOICE_DELIVERED_COMMUNICATION_STATUSES,
  CommunicationStatus.SENDING,
  CommunicationStatus.ERROR,
];

// What the document itself was sent as; reminders and receipts also carry its invoiceId but are other documents
export const INVOICE_DOCUMENT_COMMUNICATION_TYPES: CommunicationType[] = [
  CommunicationType.LEASE_PAYMENT_REQUEST,
  CommunicationType.LEASE_OPERATION_PAYMENT_REQUEST,
  CommunicationType.LEASE_CREDIT_NOTE,
  CommunicationType.LEASE_TENANT_STATEMENT,
  CommunicationType.COMPANY_INVOICE,
];

export const toInvoiceLifecycleErrorMessage = (code: InvoiceLifecycleErrorCode, detail?: string): string =>
  `${INVOICE_LIFECYCLE_ERROR_PREFIX}${code}${isNilOrEmpty(detail) ? '' : ` ${detail}`}`;

export const getInvoiceLifecycleErrorCode = (message: string | null | undefined): InvoiceLifecycleErrorCode | null => {
  const code = message?.match(INVOICE_LIFECYCLE_ERROR_PATTERN)?.[1];
  return Object.values(InvoiceLifecycleErrorCode).find((knownCode) => knownCode === code) ?? null;
};

export const getInvoiceCurrentVersion = (invoice: Pick<Invoice, 'sentVersions'>): number =>
  (invoice.sentVersions?.length ?? 0) + 1;

export const isInvoiceSentBefore = (invoice: Pick<Invoice, 'sentVersions'>): boolean =>
  !isNilOrEmpty(invoice.sentVersions);

export const getInvoiceLastSentVersion = (invoice: Pick<Invoice, 'sentVersions'>): InvoiceSentVersion | undefined =>
  maxBy(compact(invoice.sentVersions ?? []), 'version');

export const isInvoiceCommunicationSent = (status: CommunicationStatus | null | undefined): boolean =>
  !isNil(status) && INVOICE_SENT_COMMUNICATION_STATUSES.includes(status);

export const isInvoiceDocumentCommunicationType = (type: CommunicationType | null | undefined): boolean =>
  !isNil(type) && INVOICE_DOCUMENT_COMMUNICATION_TYPES.includes(type);

const PENDING_COMMUNICATION_STATUSES: CommunicationStatus[] = [
  CommunicationStatus.TO_SEND,
  CommunicationStatus.SNOOZED,
  CommunicationStatus.SCHEDULED,
  CommunicationStatus.SENDING,
  CommunicationStatus.ERROR,
];

/** A communication of an invoice document that can still go out */
export const isPendingInvoiceDocumentCommunication = ({
  type,
  invoiceId,
  status,
}: Pick<Communication, 'type' | 'invoiceId' | 'status'>): boolean =>
  isInvoiceDocumentCommunicationType(type) &&
  !isNilOrEmpty(invoiceId) &&
  PENDING_COMMUNICATION_STATUSES.includes(status);

/** Communications of the versions a reset withdrew: they still carry the invoiceId */
export const getWithdrawnCommunicationIds = (invoice: Pick<Invoice, 'sentVersions'>): Set<string> =>
  new Set(
    compact(
      (invoice.sentVersions ?? []).flatMap(({ channels }) => channels.map(({ communicationId }) => communicationId))
    )
  );

const INVOICE_TYPES_WITHOUT_RECEIPT: InvoiceType[] = [
  InvoiceType.VARIOUS_OPERATION,
  InvoiceType.VARIOUS_EXCEPTIONAL_REVENUE,
];

export const isInvoiceWithReceipt = (invoice: Pick<Invoice, 'creditNote' | 'leaseId' | 'type'>): boolean =>
  !isNil(invoice.leaseId) && !invoice.creditNote && !INVOICE_TYPES_WITHOUT_RECEIPT.includes(invoice.type);

// Set on an owner invoice by the credit note that reverses it, draft or validated
export const REVERSED_BY_CUSTOM_VALUE_NAME = 'reversedByInvoiceId';

// Stamped on the original at its reversal: REVERSED overwrites the status that unposting the credit note restores
export const STATUS_BEFORE_REVERSAL_CUSTOM_VALUE_NAME = 'statusBeforeReversal';

export const readStatusBeforeReversal = (
  customValues: CustomValue[] | CustomValueDTO[] | null | undefined
): InvoiceStatus =>
  findCustomValue(customValues, STATUS_BEFORE_REVERSAL_CUSTOM_VALUE_NAME) === InvoiceStatus.INVOICED
    ? InvoiceStatus.INVOICED
    : InvoiceStatus.POSTED;

export const SCHEDULED_FEE_PERIOD_CUSTOM_VALUE = 'SCHEDULED_FEE_PERIOD';

export const readReversedBy = (customValues: CustomValue[] | CustomValueDTO[] | null | undefined): string | null =>
  findCustomValue(customValues, REVERSED_BY_CUSTOM_VALUE_NAME);

export const getPeppolStatus = (customValues: Invoice['customValues']): string | null =>
  findCustomValue(customValues, InvoicePeppolCustomValueName.PEPPOL_STATUS_FOR_CUSTOM_VALUE);

/** The Peppol status of a send that was attempted: none for an invoice only recorded as not sent with its email */
export const getAttemptedPeppolStatus = (customValues: Invoice['customValues']): string | null => {
  const status = getPeppolStatus(customValues);
  const isRecordedNotSent =
    status === InvoicePeppolStatus.ERROR &&
    findCustomValue(customValues, InvoicePeppolCustomValueName.PEPPOL_ERROR_TYPE_FOR_CUSTOM_VALUE) ===
      PeppolErrorType.COMMUNICATION_DELETED;
  return isRecordedNotSent ? null : status;
};

export const isFailedPeppolStatus = (status?: string | null): boolean =>
  status === InvoicePeppolStatus.ERROR || status === InvoicePeppolStatus.FAILED;

export const isInvoiceDeliveredOnPeppol = (customValues: Invoice['customValues']): boolean => {
  const status = getPeppolStatus(customValues);
  return status === InvoicePeppolStatus.SENT || status === InvoicePeppolStatus.COMPLETED;
};

const DELIVERY_CHANNEL_BY_COMMUNICATION_CHANNEL: Partial<Record<CommunicationChannel, InvoiceDeliveryChannel>> = {
  [CommunicationChannel.EMAIL]: InvoiceDeliveryChannel.EMAIL,
  [CommunicationChannel.LETTER]: InvoiceDeliveryChannel.LETTER,
  [CommunicationChannel.REGISTERED_LETTER]: InvoiceDeliveryChannel.REGISTERED_LETTER,
};

/** The invoice's communications minus those archived with an earlier version at its reset to draft. */
export const getCurrentVersionCommunications = <T extends Pick<Communication, 'id'>>(
  invoice: Pick<Invoice, 'sentVersions'>,
  communications: T[]
): T[] => {
  const withdrawnCommunicationIds = getWithdrawnCommunicationIds(invoice);
  return communications.filter(({ id }) => !withdrawnCommunicationIds.has(id));
};

// Reminders carry the invoiceId too: only the document's own communications make it sent
export const isSentInvoiceDocumentCommunication = (
  communication: Pick<Communication, 'type' | 'status' | 'channel'>
): boolean =>
  isInvoiceDocumentCommunicationType(communication.type) &&
  isInvoiceCommunicationSent(communication.status) &&
  !isNil(DELIVERY_CHANNEL_BY_COMMUNICATION_CHANNEL[communication.channel]);

export const getCurrentVersionSends = <T extends Pick<Communication, 'id' | 'type' | 'status' | 'channel'>>(
  invoice: Pick<Invoice, 'sentVersions'>,
  communications: T[]
): T[] => getCurrentVersionCommunications(invoice, communications).filter(isSentInvoiceDocumentCommunication);

export const buildInvoiceSentChannels = (
  sentCommunications: Pick<Communication, 'id' | 'status' | 'channel' | 'externalStatus'>[],
  customValues: Invoice['customValues']
): InvoiceSentChannelInput[] => {
  const communicationChannels = sentCommunications.reduce<InvoiceSentChannelInput[]>((acc, communication) => {
    const channel = DELIVERY_CHANNEL_BY_COMMUNICATION_CHANNEL[communication.channel];
    if (!isNil(channel)) {
      acc.push({
        channel,
        status: communication.externalStatus ?? communication.status,
        communicationId: communication.id,
        peppolId: null,
      });
    }
    return acc;
  }, []);
  const peppolStatus = getAttemptedPeppolStatus(customValues);
  if (isNil(peppolStatus)) {
    return communicationChannels;
  }
  return [
    ...communicationChannels,
    {
      channel: InvoiceDeliveryChannel.PEPPOL,
      status: peppolStatus,
      communicationId: null,
      peppolId: findCustomValue(customValues, InvoicePeppolCustomValueName.PEPPOL_ID_FOR_CUSTOM_VALUE),
    },
  ];
};

type ManualDocumentPosting = Pick<Posting, 'type' | 'topClass' | 'totalAmount' | 'transactionId' | 'invoicePostingId'>;

// Settlement postings of a reconciliation recorded on the draft link to a line or a transaction: they are no line
export const isDocumentLinePosting = (posting: Pick<Posting, 'transactionId' | 'invoicePostingId'>): boolean =>
  isNil(posting.transactionId) && isNil(posting.invoicePostingId);

const getDocumentLinesTotal = (
  postings: ManualDocumentPosting[],
  isCounted: (posting: ManualDocumentPosting) => boolean
): number =>
  roundAtSecondDecimal(
    Math.abs(
      postings.reduce(
        (total, posting) =>
          isCounted(posting) && isDocumentLinePosting(posting) ? total + (posting.totalAmount ?? 0) : total,
        0
      )
    )
  );

/**
 * A manual document is created posting by posting and read back through the eventually consistent byInvoice index:
 * once every posting is readable, its two sides balance and its lease-account postings add up to the document amount.
 * A discount line sits on the lease-account side, so a side alone does not add up to the amount.
 */
export const areManualDocumentPostingsReadable = (
  invoice: Pick<Invoice, 'amount'>,
  postings: ManualDocumentPosting[]
): boolean => {
  const documentAmount = roundAtSecondDecimal(Math.abs(invoice.amount ?? 0));
  return (
    getDocumentLinesTotal(postings, (posting) => posting.type === PostingType.DEBIT) ===
      getDocumentLinesTotal(postings, (posting) => posting.type === PostingType.CREDIT) &&
    getDocumentLinesTotal(postings, (posting) => posting.topClass === ACCOUNT_LEASE_TOPCLASS) === documentAmount
  );
};

export const isInvoicePeppolSendInProgress = (customValues: Invoice['customValues']): boolean =>
  getPeppolStatus(customValues) === InvoicePeppolStatus.PENDING ||
  isPeppolSendClaimHeld(customValues, todayDateTime()) ||
  isPeppolResendInFlight(customValues, todayDateTime());
